import { Slot } from '@radix-ui/react-slot'
import { VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/utils'
import { Spinner } from './spinner'

export const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        unstyled: 'rounded-none',
        default:
          '[&>div]:mb-2 [&>div]:z-[3] group/active z-[1] bg-primary transition duration-300 ease-in-out transform before:h-full before:w-full before:bg-white before:border before:rounded-md before:absolute before:border-primary hover:before:bg-purple-50 before:bottom-[4px] before:right-[1.5px] before:z-[2] active:before:translate-y-[0.1rem] active:before:translate-x-[0.1rem] border-white !text-primary border',
        'default-secondary':
          '[&>div]:mb-2 [&>div]:z-[3] group/active z-[1] bg-transparent transition duration-300 ease-in-out transform before:h-full before:w-full before:bg-transparent before:border-b before:rounded-md before:absolute before:border-primary before:bottom-[4px] before:right-[1.5px] before:z-[2] active:before:translate-y-[0.1rem] active:before:translate-x-[0.1rem] !text-neutral-900 border border-primary',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        'outline-secondary':
          'hover:bg-primary/90 !bg-transparent border-primary border',
        secondary: 'hover:bg-primary/90 bg-primary text-white border-primary',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-7 px-2 py-1 text-xs',
        sm: 'h-9 rounded-md px-3',
        'input-sm': 'h-8 rounded-md px-3',
        lg: 'h-12 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      disabled,
      loading = false,
      loadingText,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        {...props}
        className={cn(buttonVariants({ variant, size, className }), 'relative')}
        ref={ref}
        disabled={disabled || loading}
        type={type}
      >
        <div className='flex flex-row items-center min-w-0 group-active/active:translate-y-[0.1rem]'>
          {loading && <Spinner size='xs' />}
          {loading && loadingText ? <span>{loadingText}</span> : props.children}
        </div>
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button }
